import { useEffect, useState, useRef } from 'react';
import { Svg } from '@snippets';
import { LoadingModal } from './LoadingModal';
import { ShareModal } from './ShareModal';
import { ResetConfirmModal } from './ResetConfirmModal';
import { SavedItems } from './SavedItems';
import { useCustomizer } from './useCustomizer';

export function CustomizerModal({
  isOpen,
  onClose,
  variantId,
  productHandle,
  settings = {},
  selectedColor,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingFailed, setLoadingFailed] = useState(false);
  const [sceneRendered, setSceneRendered] = useState(false);

  const calledOnceRef = useRef(false);
  const modalRef = useRef(null);
  const perspectiveRef = useRef(null);
  const sidebarRef = useRef(null);
  const savedItemsRef = useRef(null);
  const scriptLoadedRef = useRef(false);

  const { state, actions } = useCustomizer({
    modal: modalRef?.current,
    selectedColor,
    onClose,
  });
  const { showShareModal, showResetConfirm, showSavedItems, shareLink } = state;
  const {
    performReset,
    setShowShareModal,
    setShowResetConfirm,
    setShowSavedItems,
    handleShareEvent,
    handleAddToCartEvent,
    customizerInit,
    handleRecipeChange,
    updateButtonState,
  } = actions;

  // Listen for scene rendered event, share event and recipe changed event
  useEffect(() => {
    let timeout = null;
    const handleSceneRendered = () => {
      console.log('customizer.scene-rendered');
      setSceneRendered(true);
      setIsLoading(false);
      setLoadingFailed(false);
      document.addEventListener('customizer.add-to-cart', handleAddToCartEvent);
      document.addEventListener('customizer.tab-selected', updateButtonState);
      document.addEventListener(
        'customizer.recipe-updates-complete',
        updateButtonState
      );
      document.addEventListener('customizer.shared', handleShareEvent);
    };
    if (modalRef?.current) {
      document.addEventListener(
        'customizer.scene-rendered',
        handleSceneRendered
      );
      // Timeout for loading failure
      timeout = setTimeout(() => {
        if (!sceneRendered) {
          setLoadingFailed(true);
          setIsLoading(false);
        }
      }, 10000);
    }
    return () => {
      setSceneRendered(false);
      document.removeEventListener(
        'customizer.add-to-cart',
        handleAddToCartEvent
      );
      document.removeEventListener(
        'customizer.recipe-updates-complete',
        updateButtonState
      );
      document.removeEventListener(
        'customizer.tab-selected',
        updateButtonState
      );
      document.removeEventListener(
        'customizer.scene-rendered',
        handleSceneRendered
      );
      document.removeEventListener('customizer.shared', handleShareEvent);
      clearTimeout(timeout);
    };
  }, [modalRef?.current]);

  // Handle body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (isOpen) {
        document.documentElement.classList.add('customizer-open');
      }
    } else {
      document.body.style.overflow = '';
      document.documentElement.classList.remove('customizer-open');
    }

    return () => {
      document.body.style.overflow = '';
      document.documentElement.classList.remove('customizer-open');
    };
  }, [isOpen]);

  // Load customizer script
  useEffect(() => {
    const loadScript = () => {
      if (
        sceneRendered ||
        scriptLoadedRef?.current === true ||
        calledOnceRef?.current === true ||
        !variantId
      ) {
        setIsLoading(false);
        return;
      }
      calledOnceRef.current = true;
      // Remove existing script if any
      const existing = document.querySelector('script#customizer');
      if (existing) {
        existing.parentNode.removeChild(existing);
      }

      // Set customizer values
      if (typeof window !== 'undefined') {
        window.customizer_values = {
          product: productHandle,
          country: 'us',
        };
      }

      const script = document.createElement('script');
      script.src =
        'https://vu-customizer.s3.amazonaws.com/production/vu_customizer.js';
      script.type = 'text/javascript';
      script.fetchPriority = 'high';
      script.id = 'customizer';
      script.setAttribute('data-variant', variantId);
      script.setAttribute('data-status', 'draft');
      script.defer = true;

      script.onload = () => {
        scriptLoadedRef.current = true;
        console.log('Customizer script loaded successfully');
      };

      script.onerror = () => {
        console.error('Customizer script failed to load');
        setLoadingFailed(true);
        setIsLoading(false);
      };

      document.head.appendChild(script);
    };

    setIsLoading(true);
    setLoadingFailed(false);
    loadScript();

    return () => {
      scriptLoadedRef.current = false;
      const script = document.getElementById('customizer');
      if (script) {
        script.parentNode.removeChild(script);
      }
    };
  }, [variantId, productHandle]);

  useEffect(() => {
    if (sceneRendered && modalRef?.current) {
      customizerInit();

      document.addEventListener(
        'customizer.recipe-changed',
        handleRecipeChange
      );
    }
    return () => {
      if (
        sceneRendered &&
        modalRef?.current &&
        typeof handleRecipeChange === 'function'
      ) {
        document.removeEventListener(
          'customizer.recipe-changed',
          handleRecipeChange
        );
      }
    };
  }, [sceneRendered]);

  const brandLogo = settings?.brand_logo;
  const logo = settings?.logo;
  const showSavedItemsBtn = settings?.show_saved_items !== false;
  const showShareButton = settings?.show_share_button !== false;
  const showResetButton = settings?.show_reset_button !== false;
  const backgroundImage = settings?.background_image;

  return (
    <>
      <LoadingModal
        isActive={isLoading && isOpen}
        onClose={() => {
          setIsLoading(false);
          onClose();
        }}
        brandLogo={brandLogo}
        loadingFailed={loadingFailed}
      />

      <div
        id="customizer-modal"
        ref={modalRef}
        className={`w-[1000px] mx-auto customizer-modal ${
          isOpen && !isLoading ? '' : 'customizer-hidden'
        }`}
      >
        <div className="customizer-modal-wrapper">
          <div className="customizer-modal-body">
            <div id="customizer-root">
              <button
                id="close"
                type="button"
                aria-label="Close"
                className="customizer-modal-close w-[20px] h-[20px] flex items-center justify-center"
                onClick={() => {
                  setIsLoading(false);
                  onClose();
                }}
              >
                <Svg
                  alt="close"
                  viewBox="0 0 48 48"
                  src="/svgs/close.svg#close"
                  className="text-black"
                />
              </button>
              {/* Main Perspective */}
              <div
                id="customizer-main-perspective"
                ref={perspectiveRef}
                style={{
                  backgroundImage: backgroundImage
                    ? `url(${backgroundImage})`
                    : undefined,
                }}
              >
                {/* Top Right Buttons */}
                <div className="top-right-buttons !hidden">
                  {showSavedItemsBtn && (
                    <>
                      <button
                        type="button"
                        aria-label="Save Wishlist"
                        title="Save Wishlist"
                        id="customizer-save-button"
                      />
                      <button
                        type="button"
                        aria-label="Saved Items"
                        className="customizer-saved-item-count cursor"
                        id="customizer-saved-item-count"
                      />
                    </>
                  )}
                  {showShareButton && (
                    <button
                      type="button"
                      title="Share Link"
                      id="customizer-share-button"
                    >
                      SHARE
                    </button>
                  )}
                  {showResetButton && (
                    <button
                      type="button"
                      title="Reset"
                      id="customizer-reset-button"
                      className="customizer-reset-button"
                    >
                      RESTART
                    </button>
                  )}
                </div>

                <div className="bottom-center-items absolute z-[1] max-md:top-[16px] max-md:left-[16px] md:w-full md:bottom-[66px] items-center justify-center">
                  <div className="flex flex-col items-center justify-center">
                    <Svg
                      alt="close"
                      viewBox="0 0 41 24"
                      src="/svgs/noprecache/customizer-360-view.svg#customizer-360-view"
                      className="w-[41px] h-[41px]"
                    />
                    <span className="text-[12px] font-medium text-black w-[70px] text-center">
                      360° View Click + Drag
                    </span>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="customizer-sidebar wrapper-div" ref={sidebarRef}>
                <div className="customizer-header close">
                  <div className="customizer-brand-logo-container">
                    {brandLogo && (
                      <div className="customizer-brand-logo brand-logo">
                        <img
                          src={brandLogo}
                          alt="brand logo"
                          width={120}
                          height={120}
                          style={{ height: 'auto' }}
                        />
                      </div>
                    )}
                    <h2 className="customizer-product-name" />
                    <div className="customizer-price" />
                    <div id="customizer-summary-specs" />
                  </div>
                </div>

                <div className="customizer-sidebar-content">
                  <div className="customizer-tab-tip-body" />

                  <div id="customizer-perspectives" />

                  <div className="all-tabs-container hidden">
                    <div id="customizer-tab-container" className="all_tabs">
                      <div className="wrapper-nav">
                        <span>
                          <p>
                            <b>Steps</b>
                          </p>
                        </span>
                        <div className="progress-bar-container progress-hidden">
                          <div
                            className="progress-bar"
                            id="customizer-progress-bar"
                          />
                          <div id="progress-bar-count" />
                        </div>
                        <div id="customizer-tab-counter" />
                      </div>
                      <div className="tab-name">
                        <div id="customizer-tab-current-name" />
                      </div>
                      {/* Tabs will be rendered by the customizer script */}
                    </div>
                  </div>

                  <div id="customizer-tab-body-container">
                    <div className="customizer-tab-body-header flex relative z-0 min-h-[56px] md:min-h-[60px]">
                      <button
                        type="button"
                        className="customizer-tab-body-back-button text-[12px] md:text-sm font-medium text-black capitalize transition-all duration-300 !hidden"
                        aria-label="Back"
                      >
                        Back
                      </button>
                      <h2 className="!hidden customizer-tab-body-title text-[12px] md:text-sm font-medium text-black transition-all duration-300" />
                      <button
                        type="button"
                        title="Share Your Design"
                        className="hidden customizer-share-design text-[12px] md:text-sm font-medium text-black capitalize transition-all duration-300"
                      >
                        Share
                      </button>
                    </div>
                    <div
                      id="customizer-tab-body-preview"
                      className="customizer-tab-body"
                    >
                      <div id="customizer-summary" className="hidden md:flex-1">
                        <div
                          id="customizer-summary-items"
                          className="!hidden"
                        />
                      </div>
                      <div id="customizer-disclaimer" />
                    </div>
                  </div>
                </div>

                <div className="wrapper-bottom-nav">
                  <button
                    type="button"
                    aria-label="Clear"
                    className="customizer-clear-button"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    aria-label="Previous"
                    id="customizer-tab-prev"
                    className="hidden"
                  >
                    <span>Prev</span>
                  </button>
                  <button
                    id="customizer-tab-next"
                    className="!hidden"
                    type="button"
                    aria-label="Review Design"
                    disabled
                    aria-disabled
                  >
                    <span>Review Design</span>
                  </button>
                  <div className="customizer-atc-container flex">
                    <div id="customizer-element-quantity-container" />
                    <button
                      type="button"
                      aria-label="Add to Cart"
                      className="customizer-add-to-cart-button"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          id="overlay-popup"
          className={`${
            showShareModal || showResetConfirm ? 'block' : 'hidden'
          }`}
          role="button"
          tabIndex={0}
          onClick={() =>
            showShareModal
              ? setShowShareModal(false)
              : setShowResetConfirm(false)
          }
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              showShareModal
                ? setShowShareModal(false)
                : setShowResetConfirm(false);
            }
          }}
          onKeyUp={(e) => {
            if (e.key === 'Escape') {
              showShareModal
                ? setShowShareModal(false)
                : setShowResetConfirm(false);
            }
          }}
        />
        <ResetConfirmModal
          isOpen={showResetConfirm}
          onClose={() => setShowResetConfirm(false)}
          onConfirm={performReset}
        />

        <ShareModal
          shareLink={shareLink}
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
        />

        <SavedItems
          isOpen={showSavedItems}
          onClose={() => setShowSavedItems(false)}
          ref={savedItemsRef}
        />
      </div>
    </>
  );
}
